import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Editor, EditorState, ContentState, Modifier } from 'draft-js';
import { OrderedSet } from 'immutable';
import actions from '@src/redux/actions';
import classNames from 'classnames';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { Button, Popper } from '@mui/material';
import {
  KeyboardArrowDownOutlined,
  // KeyboardArrowUpOutlined,
} from '@mui/icons-material';
import RcTooltip from 'rc-tooltip';
import PreviewButton from '@src/components/PreviewButton';
import { TTS_GUIDE } from '@src/constants/tourGuide';
import { BREAK_TIME_REGEX, REGEX } from '@src/constants/voice';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { countTextLength } from '@src/services/tts';
import { getSelectedText } from '@src/services/sentence';
import { generateDecorator } from '@src/services/editor';
import {
  handleEditParagraphs,
  convertParagraphsToState,
  handleSelectedParagraph,
  countEditorStateLength,
  convertTextToParagraphs,
  convertParagraphsToText,
} from '@src/services/paragraph';
import Tooltip from '@src/components/Tooltip';
import { handleSelection } from '@src/services/entity';
import debounce from '@src/utils/debounce';

import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import { StyledMapEditor } from '@src/containers/TTS/index.style';

import {
  // TTS_BOX_SIZE,
  TTS_VERSION,
} from '@src/constants/tts';
import PreviewButtonWithDescription from '@src/components/PreviewButtonWithDescription';
import apis from '@src/apis';
import { checkVietNam } from '@src/utils/checkCountry';
import useUserInfo from '@src/hooks/useTtsUser';
import Sentences from './Sentences';

import {
  StyledContent,
  StyledPreviewButton,
  StyledPreviewButtonNew,
} from './index.style';
import SampleScripts from '../SampleScripts';
import { StyledIconButton } from '../Requests/index.style';
import AIEditor from '../AiEditor';

const Content = ({
  useNewRequestUI,
  children,
  openSentences,
  content,
  isSmallContent,
  inputLength,
  characterExceed,
  onChangeContent,
  requestTableHeight,
  onChangeInputLength,
  onChangeCharacterExceed,
  onchangeSynthesisSentence,
  onTryListeningSentence,
  onChangeVoice,
  audioRef,
  synthesisSentence,
  enableEditor,
  onEnableEditor,
  // onResetContentEditor,
  onChangeExpandRequestTable,
  isSMSUser,
  isNewHeaderUI,
}) => {
  const [clickedInfo, setClickedInfo] = useState({});
  const [isInputText, setIsInputText] = useState(false);
  const [isHaveScroll, setIsHaveScroll] = useState(false);
  const [sampleScripts, setSampleScripts] = useState([]);
  const [defaultSampleScript, setDefaultSampleScript] = useState(null);
  const boxRef = useRef(null);
  const {
    synthesisRequest,
    sentences,
    loadingSynthesisConfig,
    paragraphs,
    isLoadingParagraphs,
    advanceFeature,
    showSample,
    previewParagraphs: previewContent,
    positionWords,
    currentWordSelected,
    wordsAIEditorChecked,
  } = useSelector((state) => state.synthesisRequest);
  const user = useSelector((state) => state.auth.user);
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();
  const { isAudioLoading, popupPreviewAnchorEl, showAudioPlayer } = useSelector(
    (state) => state.audioPlayer,
  );

  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;
  const isVietNam = checkVietNam();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ttsRef = useRef(null);
  const { getFeatureValue } = useFeatureFlags();
  const useHeaderBanner = getFeatureValue(
    FEATURE_KEYS.HEADER_BANNER_FLASH_SALE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useNewUXSynthesis = getFeatureValue(FEATURE_KEYS.NEW_UX_SYNTHESIS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useAIEditor = getFeatureValue(FEATURE_KEYS.AI_EDITTING, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useTTSButtonLayout = getFeatureValue(FEATURE_KEYS.NEW_TTS_LAYOUT, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const { headerVoucher } = useSelector((state) => state.voucher);

  const handleChangePopupPreviewAnchorEl = (value) => {
    dispatch(actions.audioPreview.updatePopupPreviewAnchorEl(value));
  };

  const useOldSaveDraftFeatureFlag = getFeatureValue(
    FEATURE_KEYS.OLD_AUTO_SAVE_DRAFT,
  );

  const useNewTTSLayout = getFeatureValue(FEATURE_KEYS.NEW_TTS_LAYOUT, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useHighlightToPreview = getFeatureValue(
    FEATURE_KEYS.HIGHLIGHT_TO_PREVIEW,
  );

  const locationRouter = useLocation();

  const canPreview = advanceFeature
    ? previewContent?.length && usingPackage.id && !isAudioLoading
    : synthesisSentence && usingPackage.id && !isAudioLoading;

  const handleOpenPreviewButton = () => {
    const selection = window.getSelection();

    const getBoundingClientRect = () => {
      if (selection && selection.rangeCount > 0) {
        const clientRects = selection?.getRangeAt(0)?.getClientRects();
        const position = clientRects[clientRects.length - 1];
        const fixPosition = new DOMRect(
          (position?.left || 0) + 3,
          position?.top || 0,
          position?.width || 0,
          position?.height || 0,
        );
        return fixPosition || {};
      }
      handleChangePopupPreviewAnchorEl(null);
      return {};
    };

    handleChangePopupPreviewAnchorEl({ getBoundingClientRect });
  };

  const fetchSampleScript = async () => {
    const response = await apis.sampleScripts.getSampleScript();
    const totalSampleScript = response?.result?.sampleScripts;
    let sampleScript = [];
    if (isVietNam)
      sampleScript = totalSampleScript.filter(
        (item) => item.isVietnamese && !item.isDefault,
      );
    else
      sampleScript = totalSampleScript.filter(
        (item) => !item.isVietnamese && !item.isDefault,
      );
    setSampleScripts(sampleScript);

    const defaultSample = totalSampleScript.find(
      (item) =>
        item.isDefault && (isVietNam ? item.isVietnamese : !item.isVietnamese),
    );
    setDefaultSampleScript(defaultSample);
    dispatch(actions.synthesisRequest.updateDefaultSample(defaultSample));
  };

  useEffect(() => {
    fetchSampleScript();
  }, [isVietNam]);

  const handleChangeEditor = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const lengthContent = countTextLength(contentState.getPlainText());

    if (contentState.getPlainText() !== synthesisRequest?.text) {
      onEnableEditor(true);
      if (!enableEditor)
        dispatch(
          actions.synthesisRequest.cloneSynthesisRequest(useNewUXSynthesis),
        );
    }

    onChangeInputLength(lengthContent);

    let newContent = editorState;
    const currentStyle = editorState.getCurrentInlineStyle();
    if (currentStyle && currentStyle.size) {
      newContent = EditorState.setInlineStyleOverride(
        editorState,
        OrderedSet.of(),
      );
    }
    onChangeContent(newContent);

    if (lengthContent > maxLengthInputText) {
      onChangeCharacterExceed(true);
    } else {
      onChangeCharacterExceed(false);
    }

    const selectedText = getSelectedText(editorState);
    onchangeSynthesisSentence(selectedText);
  };

  // Define event when editor state changes
  const handleChangeEditorState = (newEditorState) => {
    const selectionState = newEditorState.getSelection();
    // If dont focus on the editor, cant do anything
    if (!selectionState.toJS().hasFocus) {
      onChangeContent(newEditorState);
      return;
    }
    if (!isInputText) setIsInputText(true);

    const contentState = newEditorState.getCurrentContent();

    const currentPlainText = synthesisRequest?.text
      ?.replace(REGEX.ADVANCE_TAG, '')
      .trim();
    const isTextModifiedNew =
      convertParagraphsToText(paragraphs) !== synthesisRequest?.text;
    const isTextModifiedOld =
      contentState.getPlainText()?.trim() !== currentPlainText;

    const compareText =
      useNewUXSynthesis && advanceFeature
        ? isTextModifiedNew
        : isTextModifiedOld;
    if (compareText) {
      onEnableEditor(true);
      if (!enableEditor)
        dispatch(
          actions.synthesisRequest.cloneSynthesisRequest(useNewUXSynthesis),
        );
    }

    // Selection area when the user highlights
    const selection = handleSelection({
      selectionState,
      contentState,
      paragraphs,
    });
    if (selection) {
      onChangeContent(EditorState.forceSelection(newEditorState, selection));
      // previewParagraphs: preview data, format same paragraphs
      // selectedParagraphs: selected data, format same content editor state to support for emphasis, speed, break time
      const { previewParagraphs, selectedParagraph } = handleSelectedParagraph(
        selection,
        contentState,
        paragraphs,
      );
      dispatch(
        actions.synthesisRequest.updateSelectedParagraph(selectedParagraph),
      );
      dispatch(
        actions.synthesisRequest.updatePreviewParagraphs(previewParagraphs),
      );
    } else {
      dispatch(actions.synthesisRequest.updatePreviewParagraphs([]));
      onChangeContent(newEditorState);
    }
  };

  const handleChangeParagraphs = () => {
    // Update paragraphs when editor state changes
    const newParagraphs = handleEditParagraphs(paragraphs, content);
    if (newParagraphs !== null) {
      dispatch(actions.synthesisRequest.updateParagraphs(newParagraphs));

      const contentLength = countEditorStateLength(content, newParagraphs);

      onChangeInputLength(contentLength);

      if (contentLength > maxLengthInputText) {
        onChangeCharacterExceed(true);
      } else {
        onChangeCharacterExceed(false);
      }
    }
    setIsInputText(false);
  };

  const handleBlurEditor = () => {
    if (advanceFeature) {
      dispatch(actions.synthesisRequest.updatePreviewParagraphs([]));
    } else {
      onchangeSynthesisSentence();
    }
  };

  useEffect(() => {
    if (advanceFeature && isInputText)
      debounce(handleChangeParagraphs, 300)(content);
  }, [content]);

  useEffect(() => {
    // loadingSynthesisConfig: load again tts when user click to request in request table or change voice
    if (loadingSynthesisConfig) {
      const {
        text,
        sentences: synthesisConfigSentences,
        characters,
        voice,
        paragraphs: synthesisParagraphs = [],
        id,
      } = synthesisRequest;

      if (id) {
        onChangeInputLength(characters);
      }
      onChangeVoice(voice, loadingSynthesisConfig);

      // Reset audio
      // dispatch(
      //   actions.audioPlayer.updateMetaData({ currentTime: 0, duration: 0 }),
      // );
      // dispatch(actions.audioPlayer.updateStatus(false));
      // const audioLink = synthesisRequest.audioLink || '';
      // dispatch(actions.audioPlayer.updateAudioLink(audioLink));
      dispatch(actions.synthesisRequest.updateLoadingSynthesisConfig(false));

      const processBySentence = !!synthesisConfigSentences?.length;
      // When process method change => update open sentence
      if (processBySentence !== openSentences)
        dispatch(
          actions.synthesisRequest.updateOpenSentences(processBySentence),
        );

      // Sentences processing
      if (processBySentence) {
        const convertSentences = synthesisConfigSentences.map((item) => ({
          ...item,
          id: uuidv4(),
          onLoad: true,
          text: item.text,
        }));

        dispatch(actions.synthesisRequest.updateSentences(convertSentences));
        return;
      }

      // Paragraph process
      const isAdvanceFeature = voice?.version === TTS_VERSION.EMPHASIS;
      if (!isAdvanceFeature) {
        const contentEditor = EditorState.createWithContent(
          ContentState.createFromText(text || ''),
          generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
        );
        const newEditorState = EditorState.moveFocusToEnd(contentEditor);
        onChangeContent(newEditorState);
      }

      if (isAdvanceFeature && isAdvanceFeature === advanceFeature) {
        dispatch(
          actions.synthesisRequest.updateParagraphs(synthesisParagraphs),
        );
        dispatch(actions.synthesisRequest.updateLoadingParagraphs(true));
      }

      if (isAdvanceFeature !== advanceFeature)
        dispatch(
          actions.synthesisRequest.switchAdvanceFeature(isAdvanceFeature),
        );
    }
  }, [loadingSynthesisConfig]);

  const handleShowContent = () => {
    let contentParagraphs = paragraphs;
    if (!paragraphs?.length) {
      contentParagraphs = convertTextToParagraphs(synthesisRequest?.text || '');
    }
    const contentState = convertParagraphsToState(contentParagraphs);
    onChangeContent(EditorState.push(content, contentState));
  };
  // const handleChangeTtsBoxSize = (value) => setTtsBoxSize(value);

  const handleResetContent = () => {
    dispatch(actions.synthesisRequest.updateParagraphs([]));
    dispatch(actions.synthesisRequest.updateSelectedSentences([]));

    if (useOldSaveDraftFeatureFlag) {
      dispatch(actions.synthesisRequest.updateOpenSentences(false));
      dispatch(actions.synthesisRequest.switchAdvanceFeature(false));
    }
  };

  const placeholder = useMemo(
    () => defaultSampleScript?.text || '',
    [defaultSampleScript],
  );

  useEffect(() => {
    // isLoadingParagraphs: Load again editor state when paragraphs change
    if (isLoadingParagraphs) {
      handleShowContent();
      dispatch(actions.synthesisRequest.updateLoadingParagraphs(false));
    }
  }, [isLoadingParagraphs]);

  useEffect(() => {
    if (advanceFeature && !openSentences) {
      dispatch(
        actions.synthesisRequest.updateParagraphs(synthesisRequest?.paragraphs),
      );
      dispatch(actions.synthesisRequest.updateLoadingParagraphs(true));
    }
  }, [advanceFeature]);

  useEffect(() => {
    const box = boxRef.current;
    if (box.scrollHeight > box.clientHeight) {
      setIsHaveScroll(true);
    } else {
      setIsHaveScroll(false);
    }
  }, [inputLength, content, sentences.length]);

  useEffect(() => {
    dispatch(actions.synthesisRequest.updateLoadingParagraphs(false));
    return () => {
      handleResetContent();
    };
  }, [locationRouter]);

  useEffect(() => {
    if (!openSentences) {
      dispatch(actions.synthesisRequest.updateSelectedSentences([]));
    }
  }, [openSentences]);

  useEffect(() => {
    if (!canPreview) {
      handleChangePopupPreviewAnchorEl(null);
      return;
    }
    if (
      useHighlightToPreview &&
      (synthesisSentence || !!previewContent.length)
    ) {
      debounce(handleOpenPreviewButton, 300)();
    }
  }, [synthesisSentence, previewContent]);

  const handlePastedText = () => {
    const currentScrollHeight = boxRef.current?.scrollHeight;
    setTimeout(() => {
      const newScrollHeight = boxRef.current?.scrollHeight;
      boxRef.current.scrollTop += newScrollHeight - currentScrollHeight;
    }, 100);
  };

  const handleTryListeningSentence = (sentence) => {
    handleChangePopupPreviewAnchorEl(null);
    onTryListeningSentence(sentence);
  };

  useEffect(() => {
    if (inputLength && showSample)
      dispatch(actions.synthesisRequest.updateShowSampleScripts(false));
  }, [inputLength]);

  const handleWordClick = useCallback((editorState) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();

    // Xác định block tại vị trí click
    const blockKey = selection.getAnchorKey(); // key của block được click
    const block = contentState.getBlockForKey(blockKey);

    const offset = selection.getAnchorOffset(); // Vị trí con trỏ trong block
    const blockText = block.getText(); // Lấy toàn bộ nội dung của block
    // Tìm các từ trong block
    const words = blockText.split(/\s+/);
    let wordStartPosition = 0; // Vị trí bắt đầu của từ trong văn bản (block)
    let decoratedText = '';
    let currentPosition = 0; // Vị trí hiện tại tính từ đầu  block

    words.forEach((word) => {
      // Tính vị trí bắt đầu và kết thúc của từ
      const wordEndPosition = currentPosition + word.length;

      // Kiểm tra xem con trỏ có nằm trong từ này không
      if (offset > currentPosition && offset < wordEndPosition) {
        decoratedText = word;
        wordStartPosition = currentPosition;
      }
      // Cập nhật vị trí hiện tại cho từ tiếp theo
      currentPosition = wordEndPosition + 1; // Cộng thêm 1 cho dấu cách
    });
    const editorElement = boxRef?.current;
    const range = window?.getSelection()?.getRangeAt(0); // Lấy range của con trỏ
    const rect = range?.getBoundingClientRect(); // Tọa độ của từ trong viewport
    // Tính x, y dựa trên vị trí của editor
    const editorRect = editorElement.getBoundingClientRect();
    const x = rect.left - editorRect.left;
    const y = rect.top - editorRect.top;
    // Trả về vị trí bắt đầu của từ và từ đó
    return { wordStartPosition, decoratedText, x, y, blockKey };
  }, []);

  const handleClickEditor = () => {
    if (!useAIEditor) return;
    const { wordStartPosition, decoratedText, x, y, blockKey } =
      handleWordClick(content);
    if (decoratedText?.includes(wordsAIEditorChecked.join(' '))) {
      dispatch(
        actions.synthesisRequest.updateCurrentSelectedWord({
          start: wordStartPosition,
          decoratedText,
          blockKey,
        }),
      );
      setClickedInfo({
        clickedWord: decoratedText,
        wordStartPosition,
        x,
        y,
      });
    }
  };
  const replaceWord = (text) => {
    const contentState = content.getCurrentContent();
    const selection = content.getSelection().merge({
      anchorKey: currentWordSelected?.blockKey,
      focusKey: currentWordSelected?.blockKey,
      anchorOffset: currentWordSelected?.start,
      focusOffset:
        currentWordSelected?.start + currentWordSelected?.decoratedText.length,
    });

    const newContentState = Modifier.replaceText(contentState, selection, text);

    onChangeContent(
      EditorState.push(content, newContentState, 'insert-characters'),
    );
    dispatch(actions.synthesisRequest.updateCurrentSelectedWord(null));
  };

  return (
    <StyledContent
      ref={ttsRef}
      useNewTTSLayout={useNewTTSLayout}
      requestTableHeight={requestTableHeight}
      showaudioplayer={showAudioPlayer}
      isSmallContent={isSmallContent}
      useNewRequestUI={useNewRequestUI}
      id={TTS_GUIDE.ENTER_TEXT_HERE}
      openPreviewGuide={useHighlightToPreview && !openSentences}
      useHeaderBanner={useHeaderBanner}
      openSentences={openSentences}
      showSample={showSample}
      headerVoucher={headerVoucher}
      isNewHeaderUI={isNewHeaderUI}
    >
      {openSentences ? (
        <Sentences
          advanceFeature={advanceFeature}
          isSmallContent={isSmallContent}
          useNewRequestUI={useNewRequestUI}
          useNewTTSLayout={useNewTTSLayout}
          requestTableHeight={requestTableHeight}
          characterExceed={characterExceed}
          onChangeInputLength={onChangeInputLength}
          onChangeCharacterExceed={onChangeCharacterExceed}
          onTryListeningSentence={onTryListeningSentence}
          audioRef={audioRef}
          openSentences={openSentences}
          enableEditor={enableEditor}
          onEnableEditor={onEnableEditor}
          editorRef={boxRef}
          useHeaderBanner={useHeaderBanner}
          headerVoucher={headerVoucher}
          isNewHeaderUI={isNewHeaderUI}
        />
      ) : (
        <div
          id="editor-wrapper"
          onClick={handleClickEditor}
          role="button"
          tabIndex={0}
          className={classNames(
            useNewRequestUI || useNewTTSLayout
              ? 'editor-wrapper-new-layout'
              : 'editor-wrapper',
            {
              'character-exceed': characterExceed,
            },
          )}
          ref={boxRef}
          onBlur={handleBlurEditor}
        >
          <Editor
            placeholder={placeholder}
            editorState={content}
            onChange={
              advanceFeature ? handleChangeEditorState : handleChangeEditor
            }
            customStyleMap={StyledMapEditor}
            stripPastedStyles
            handlePastedText={handlePastedText}
          />
          {useAIEditor && (
            <RcTooltip
              visible={positionWords?.find(
                (w) => w?.start === currentWordSelected?.start,
              )}
              overlayStyle={{
                opacity: 1,
                padding: '0px 5px 0px 8px',
              }}
              onVisibleChange={(visible) => {
                if (!visible) {
                  dispatch(
                    actions.synthesisRequest.updateCurrentSelectedWord(null),
                  );
                }
              }}
              overlayInnerStyle={{ background: 'white' }}
              destroyTooltipOnHide
              overlay={
                <AIEditor.ActionsEdit
                  onReplaceWord={(text) => replaceWord(text)}
                />
              } // Nội dung tooltip
              key={`${clickedInfo?.left}-${clickedInfo?.top}`}
              trigger={['click']}
            >
              <span
                style={{
                  position: 'absolute',
                  left: clickedInfo?.x,
                  top: clickedInfo?.y,
                }}
              />
            </RcTooltip>
          )}
        </div>
      )}
      <div
        className={
          useNewRequestUI
            ? 'preview-guide preview-guide-new-ui'
            : 'preview-guide'
        }
      >
        {isSmallContent && isHaveScroll && (
          <>
            {useNewRequestUI ? (
              <Tooltip arrow title={t('expand')} placement="top">
                <StyledIconButton
                  className="new-preview-guide-btn"
                  onClick={onChangeExpandRequestTable}
                >
                  <KeyboardArrowDownOutlined />
                </StyledIconButton>
              </Tooltip>
            ) : (
              <Button
                size="small"
                variant="outlined"
                className="change-size-editor"
                onClick={onChangeExpandRequestTable}
                endIcon={<KeyboardArrowDownOutlined className="expand-icon" />}
              >
                {t('expand')}
              </Button>
            )}
          </>
        )}
      </div>
      <StyledPreviewButtonNew
        isTTSButtonLayout={useTTSButtonLayout}
        openSentences={openSentences}
      >
        <div className="tool-row">
          {showSample ? (
            <SampleScripts
              audioRef={audioRef}
              sampleScripts={sampleScripts}
              onChangeInputLength={onChangeInputLength}
            />
          ) : (
            useTTSButtonLayout &&
            (useAIEditor ? (
              <div className="ai-editor-wrapper">
                <AIEditor.DropdownAction />
                <PreviewButtonWithDescription
                  isSMSUser={isSMSUser}
                  isPreviewBtnBig={useTTSButtonLayout}
                  synthesisRequest={synthesisRequest}
                  synthesisSentence={synthesisSentence}
                  openSentences={openSentences}
                  onTryListeningSentence={onTryListeningSentence}
                  audioRef={audioRef}
                  canPreview={canPreview}
                />
              </div>
            ) : (
              <PreviewButtonWithDescription
                isSMSUser={isSMSUser}
                isPreviewBtnBig={useTTSButtonLayout}
                synthesisRequest={synthesisRequest}
                synthesisSentence={synthesisSentence}
                openSentences={openSentences}
                onTryListeningSentence={onTryListeningSentence}
                audioRef={audioRef}
                canPreview={canPreview}
              />
            ))
          )}
          {!useTTSButtonLayout && (
            <PreviewButtonWithDescription
              isSMSUser={isSMSUser}
              synthesisRequest={synthesisRequest}
              synthesisSentence={synthesisSentence}
              openSentences={openSentences}
              onTryListeningSentence={onTryListeningSentence}
              audioRef={audioRef}
              canPreview={canPreview}
            />
          )}
          {children}
        </div>
      </StyledPreviewButtonNew>
      {/* <RefreshButton
        inputLength={inputLength}
        enableEditor={enableEditor}
        onResetContentEditor={onResetContentEditor}
      /> */}
      <Popper
        open={Boolean(popupPreviewAnchorEl)}
        anchorEl={popupPreviewAnchorEl}
        placement="right"
        style={{ zIndex: 1, cursor: 'pointer' }}
      >
        <StyledPreviewButton>
          <PreviewButton
            synthesisRequest={synthesisRequest}
            synthesisSentence={synthesisSentence}
            onTryListeningSentence={handleTryListeningSentence}
            audioRef={audioRef}
            canPreview={canPreview}
            isPopupPreview
          />
        </StyledPreviewButton>
      </Popper>
      {/* <ChangeBoxTTSSizeButton
        isShow={isHaveScroll}
        ttsBoxSize={ttsBoxSize}
        toolbarRef={toolbarRef}
        onChangeTtsBoxSize={handleChangeTtsBoxSize}
          /> */}
    </StyledContent>
  );
};

export default Content;
